// src/store/slices/uiSlice.ts
import {createSlice, type PayloadAction} from "@reduxjs/toolkit";
import {STORAGE_KEYS} from "../../utils/constants";
import type {Language} from "../../types";

interface ToastMessage {
  id: string;
  type: "success" | "error" | "warning" | "info";
  title: string;
  message?: string;
  duration?: number;
}

interface ConfirmDialog {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "warning" | "info";
  onConfirm?: () => void;
  onCancel?: () => void;
}

interface UIState {
  // Theme & Display
  isDarkMode: boolean;
  language: Language;
  sidebarCollapsed: boolean;

  // Loading States
  globalLoading: boolean;
  pageLoading: boolean;

  // UI Feedback
  toasts: ToastMessage[];
  confirmDialog: ConfirmDialog;

  // Table & Pagination
  currentPage: Record<string, number>;
  pageSize: Record<string, number>;
  sortConfig: Record<string, {key: string; direction: "asc" | "desc"}>;

  // Filters
  filters: Record<string, Record<string, unknown>>;

  // Modal States
  modals: Record<string, boolean>;
}

const initialState: UIState = {
  isDarkMode: localStorage.getItem(STORAGE_KEYS.THEME) === "dark",
  language: (localStorage.getItem(STORAGE_KEYS.LANGUAGE) as Language) || "eng",
  sidebarCollapsed: JSON.parse(
    localStorage.getItem(STORAGE_KEYS.SIDEBAR_COLLAPSED) || "false"
  ),

  globalLoading: false,
  pageLoading: false,

  toasts: [],
  confirmDialog: {
    isOpen: false,
    title: "",
    message: "",
  },

  currentPage: {},
  pageSize: {},
  sortConfig: {},
  filters: {},
  modals: {},
};

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    // Theme & Display
    toggleDarkMode: (state) => {
      state.isDarkMode = !state.isDarkMode;
      localStorage.setItem(
        STORAGE_KEYS.THEME,
        state.isDarkMode ? "dark" : "light"
      );

      // Apply to document
      if (state.isDarkMode) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    },

    setDarkMode: (state, action: PayloadAction<boolean>) => {
      state.isDarkMode = action.payload;
      localStorage.setItem(
        STORAGE_KEYS.THEME,
        action.payload ? "dark" : "light"
      );

      if (action.payload) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    },

    setLanguage: (state, action: PayloadAction<Language>) => {
      state.language = action.payload;
      localStorage.setItem(STORAGE_KEYS.LANGUAGE, action.payload);
    },

    toggleSidebar: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
      localStorage.setItem(
        STORAGE_KEYS.SIDEBAR_COLLAPSED,
        JSON.stringify(state.sidebarCollapsed)
      );
    },

    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload;
      localStorage.setItem(
        STORAGE_KEYS.SIDEBAR_COLLAPSED,
        JSON.stringify(action.payload)
      );
    },

    // Loading States
    setGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.globalLoading = action.payload;
    },

    setPageLoading: (state, action: PayloadAction<boolean>) => {
      state.pageLoading = action.payload;
    },

    // Toast Messages
    addToast: (state, action: PayloadAction<Omit<ToastMessage, "id">>) => {
      const id = Date.now().toString();
      state.toasts.push({
        id,
        duration: 5000,
        ...action.payload,
      });
    },

    removeToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter(
        (toast) => toast.id !== action.payload
      );
    },

    clearToasts: (state) => {
      state.toasts = [];
    },

    // Confirm Dialog
    showConfirmDialog: (
      state,
      action: PayloadAction<Omit<ConfirmDialog, "isOpen">>
    ) => {
      state.confirmDialog = {
        isOpen: true,
        confirmText: "Confirm",
        cancelText: "Cancel",
        variant: "info",
        ...action.payload,
      };
    },

    hideConfirmDialog: (state) => {
      state.confirmDialog = {
        isOpen: false,
        title: "",
        message: "",
      };
    },

    // Table & Pagination
    setCurrentPage: (
      state,
      action: PayloadAction<{key: string; page: number}>
    ) => {
      const {key, page} = action.payload;
      console.log('Redux setCurrentPage:', {key, page, previousPage: state.currentPage[key]});
      state.currentPage[key] = page;
    },

    setPageSize: (
      state,
      action: PayloadAction<{key: string; size: number}>
    ) => {
      const {key, size} = action.payload;
      state.pageSize[key] = size;
      // Reset to first page when changing page size
      state.currentPage[key] = 1;
    },

    setSortConfig: (
      state,
      action: PayloadAction<{
        key: string;
        sortKey: string;
        direction: "asc" | "desc";
      }>
    ) => {
      const {key, sortKey, direction} = action.payload;
      state.sortConfig[key] = {key: sortKey, direction};
    },

    clearSortConfig: (state, action: PayloadAction<string>) => {
      delete state.sortConfig[action.payload];
    },

    // Filters
    setFilters: (
      state,
      action: PayloadAction<{key: string; filters: Record<string, unknown>}>
    ) => {
      const {key, filters} = action.payload;
      state.filters[key] = filters;
      // Reset to first page when filters change
      state.currentPage[key] = 1;
    },

    updateFilter: (
      state,
      action: PayloadAction<{key: string; filterKey: string; value: unknown}>
    ) => {
      const {key, filterKey, value} = action.payload;
      console.log('Redux updateFilter:', {key, filterKey, value, willResetPage: true});
      if (!state.filters[key]) {
        state.filters[key] = {};
      }
      state.filters[key][filterKey] = value;
      // Reset to first page when filter changes
      state.currentPage[key] = 1;
    },

    clearFilters: (state, action: PayloadAction<string>) => {
      delete state.filters[action.payload];
      state.currentPage[action.payload] = 1;
    },

    clearFilter: (
      state,
      action: PayloadAction<{key: string; filterKey: string}>
    ) => {
      const {key, filterKey} = action.payload;
      if (state.filters[key]) {
        delete state.filters[key][filterKey];
      }
    },

    // Modal States
    openModal: (state, action: PayloadAction<string>) => {
      state.modals[action.payload] = true;
    },

    closeModal: (state, action: PayloadAction<string>) => {
      state.modals[action.payload] = false;
    },

    toggleModal: (state, action: PayloadAction<string>) => {
      const modalKey = action.payload;
      state.modals[modalKey] = !state.modals[modalKey];
    },

    // Reset UI State
    resetUIState: (state) => {
      state.currentPage = {};
      state.pageSize = {};
      state.sortConfig = {};
      state.filters = {};
      state.modals = {};
      state.toasts = [];
      state.confirmDialog = {
        isOpen: false,
        title: "",
        message: "",
      };
    },

    // Initialize UI
    initializeUI: (state) => {
      // Initialize theme
      const theme = localStorage.getItem(STORAGE_KEYS.THEME);
      if (theme === "dark") {
        state.isDarkMode = true;
        document.documentElement.classList.add("dark");
      } else {
        state.isDarkMode = false;
        document.documentElement.classList.remove("dark");
      }

      // Initialize language
      const language = localStorage.getItem(STORAGE_KEYS.LANGUAGE) as Language;
      if (language) {
        state.language = language;
      }

      // Initialize sidebar state
      const sidebarCollapsed = localStorage.getItem(
        STORAGE_KEYS.SIDEBAR_COLLAPSED
      );
      if (sidebarCollapsed) {
        state.sidebarCollapsed = JSON.parse(sidebarCollapsed);
      }
    },
  },
});

export const {
  // Theme & Display
  toggleDarkMode,
  setDarkMode,
  setLanguage,
  toggleSidebar,
  setSidebarCollapsed,

  // Loading States
  setGlobalLoading,
  setPageLoading,

  // Toast Messages
  addToast,
  removeToast,
  clearToasts,

  // Confirm Dialog
  showConfirmDialog,
  hideConfirmDialog,

  // Table & Pagination
  setCurrentPage,
  setPageSize,
  setSortConfig,
  clearSortConfig,

  // Filters
  setFilters,
  updateFilter,
  clearFilters,
  clearFilter,

  // Modal States
  openModal,
  closeModal,
  toggleModal,

  // Reset & Initialize
  resetUIState,
  initializeUI,
} = uiSlice.actions;

export default uiSlice.reducer;

// Selectors
export const selectIsDarkMode = (state: {ui: UIState}) => state.ui.isDarkMode;
export const selectLanguage = (state: {ui: UIState}) => state.ui.language;
export const selectSidebarCollapsed = (state: {ui: UIState}) =>
  state.ui.sidebarCollapsed;
export const selectGlobalLoading = (state: {ui: UIState}) =>
  state.ui.globalLoading;
export const selectPageLoading = (state: {ui: UIState}) => state.ui.pageLoading;
export const selectToasts = (state: {ui: UIState}) => state.ui.toasts;
export const selectConfirmDialog = (state: {ui: UIState}) =>
  state.ui.confirmDialog;
export const selectCurrentPage = (key: string) => (state: {ui: UIState}) =>
  state.ui.currentPage[key] || 1;
export const selectPageSize = (key: string) => (state: {ui: UIState}) =>
  state.ui.pageSize[key] || 25;
export const selectSortConfig = (key: string) => (state: {ui: UIState}) =>
  state.ui.sortConfig[key];
export const selectFilters = (key: string) => (state: {ui: UIState}) =>
  state.ui.filters[key] || {};
export const selectModalOpen = (key: string) => (state: {ui: UIState}) =>
  state.ui.modals[key] || false;
